/**
 * @module builder
 */
import type { LineItem, OptionsDocument, QuoteDocument } from 'paintscout'

import type { UpdateableLineItem, UpdateableProduct, QuoteItemSection } from '../../index'
import { moveTo } from '../../util'
import { insertOrderItem } from '../../util/insert-order-item/insert-order-item'
import { calculateQuote } from '../../util/calculate/calculate-quote'
import { uuid } from '../../../util'

export function updateLineItem(args: {
  quote: QuoteDocument
  item: UpdateableLineItem
  options: OptionsDocument
  calculate?: boolean
  insertBeforeKey?: string
  insertAfterKey?: string
  itemSection?: QuoteItemSection
}): QuoteDocument {
  const { quote, options, item, calculate, insertBeforeKey, insertAfterKey, itemSection } = args

  const key = item?.key ?? uuid()
  const section = item?.section ?? itemSection ?? 'bid'
  const isNewItem = quote.lineItems[key] ? false : true

  let originalItem = isNewItem ? { ...createBaseLineItem(key), section } : quote.lineItems[key]
  originalItem = moveTo(originalItem, section)
  const newItem = mergeLineItem({ key, originalItem, updateableLineItem: item })

  if (item.quoteFiles) {
    quote.files = { ...quote.files, ...item.quoteFiles }
  }

  const orderItem = { key, type: 'line_item' }

  // Each order array is resolved against itself - an anchor key from one view is not
  // guaranteed to exist in the other view's order array (#23892).
  const areaOrder = isNewItem
    ? insertOrderItem({ order: quote.order.area, orderItem, insertBeforeKey, insertAfterKey })
    : [...quote.order.area]
  const substrateOrder = isNewItem
    ? insertOrderItem({ order: quote.order.substrate, orderItem, insertBeforeKey, insertAfterKey })
    : [...quote.order.substrate]

  const updatedQuote = {
    ...quote,
    lineItems: {
      ...quote.lineItems,
      [key]: newItem
    },
    order: {
      ...quote.order,
      area: areaOrder,
      substrate: substrateOrder
    }
  }

  return calculate === false && !['hourlyRate', 'quantity'].includes(item.calculateBy)
    ? updatedQuote
    : calculateQuote({ quote: updatedQuote, options })
}

export function createBaseLineItem(key: string): LineItem {
  return {
    key,
    show: true,
    show_price: true,
    show_crew: true,
    use_total: true,
    show_total: true,
    name: '',
    price: 0,
    hours: 0,
    customHourlyRate: false,
    hourlyRate: 0,
    description: '',
    crew_note: '',
    client_note: '',
    calculateBy: 'none',
    materials: [] as UpdateableProduct[],
    totals: {
      materials: 0,
      afterMaterials: 0
    },
    files: []
  }
}

export function mergeLineItem({
  key,
  originalItem,
  updateableLineItem
}: {
  key: string
  originalItem: LineItem
  updateableLineItem: UpdateableLineItem
}): LineItem {
  return {
    ...originalItem,
    key,
    optionKey: updateableLineItem?.optionKey ?? '',
    name: updateableLineItem?.label ?? '',
    description: updateableLineItem?.description,
    crew_note: updateableLineItem.crewNote,
    client_note: updateableLineItem.clientNote,
    hours:
      typeof updateableLineItem.hours === 'string'
        ? Number.parseFloat(updateableLineItem.hours)
        : updateableLineItem.hours,
    price:
      typeof updateableLineItem.price === 'string'
        ? Number.parseFloat(updateableLineItem.price)
        : updateableLineItem.price,
    calculate: updateableLineItem.calculate,
    calculateBy: updateableLineItem.calculateBy,
    quantity: updateableLineItem.quantity,
    hoursPerUnit: updateableLineItem.hoursPerUnit,
    pricePerUnit: updateableLineItem.pricePerUnit,
    show_hours_for_work_order: updateableLineItem.showHoursForWorkOrder,
    images: (updateableLineItem as any).images, // legacy
    files: updateableLineItem.files,
    source: updateableLineItem.source,
    customHourlyRate: updateableLineItem.customHourlyRate,
    hideOnWorkOrder: updateableLineItem.hideOnWorkOrder,
    hourlyRate:
      typeof updateableLineItem.hourlyRate === 'string'
        ? Number.parseFloat(updateableLineItem.hourlyRate)
        : updateableLineItem.hourlyRate,
    materials: updateableLineItem.materials ? [...updateableLineItem.materials] : [],
    quoteType: updateableLineItem.quoteType,
    taxRate: updateableLineItem.taxRate,
    showPrice: updateableLineItem.showPrice,
    hideQuantity: updateableLineItem.hideQuantity
  }
}
